import { useState, useEffect, useRef } from "react";
import { MagnifyingGlass, Plus, SpinnerGap, Package } from "@phosphor-icons/react";
import { myApi } from "../../api";
import { gaEcommerce } from "@/lib/ga";

const fmt = (v) =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v || 0);

/**
 * Search box with live results to add products to the cart.
 * Debounces input (300ms) and shows a compact dropdown of matches.
 */
export default function ProductSearchBar({ onAdded, disabled = false }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [error, setError] = useState("");
  const wrapRef = useRef(null);

  // Debounced search
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const data = await myApi.shopProductSearch(term);
        setResults(data?.items || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [q]);

  // Click outside -> close
  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const handleAdd = async (prod) => {
    setAddingId(prod.ID_Produto);
    setError("");
    try {
      const r = await myApi.cartAdd(prod.ID_Produto, 1, "soma");
      if (r?.success === false) setError(r.message || "Não foi possível adicionar o produto.");
      else {
        gaEcommerce("add_to_cart",
          [{ item_id: String(prod.ID_Produto), item_name: prod.Descricao || "Produto", item_brand: "IHTP", price: Number(prod.PrecoUnitario) || 0, quantity: 1 }],
          Number(prod.PrecoUnitario) || 0);
        setQ(""); setResults([]); setOpen(false); onAdded?.();
      }
    } catch (e) {
      setError(e.message || "Erro ao adicionar ao carrinho.");
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="product-search-bar">
      <div className="bg-white rounded-xl border border-[#E5E7EB] p-2 flex items-center gap-2 shadow-sm">
        <MagnifyingGlass size={18} className="text-[#6B7280] ml-2 shrink-0" />
        <input type="text" value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder="Procurar produto por nome ou referência…"
          data-testid="product-search-input"
          disabled={disabled}
          className="flex-1 px-1 py-2 text-sm bg-transparent outline-none text-[#0F1829] placeholder:text-[#9CA3AF]" />
        {loading && <SpinnerGap size={16} className="text-[#6B7280] animate-spin mr-2" />}
      </div>

      {error && (
        <div data-testid="product-search-error" className="mt-2 p-2 rounded-md bg-red-50 border border-red-200 text-[#FF2B2B] text-xs">
          {error}
        </div>
      )}

      {open && results.length > 0 && (
        <div data-testid="product-search-results"
          className="absolute left-0 right-0 mt-1 bg-white rounded-xl border border-[#E5E7EB] shadow-xl z-20 max-h-[360px] overflow-y-auto">
          {results.map((p) => (
            <button key={p.ID_Produto} type="button"
              onClick={() => handleAdd(p)} disabled={addingId === p.ID_Produto}
              data-testid={`product-search-result-${p.ID_Produto}`}
              className="w-full flex items-center gap-3 p-3 hover:bg-[#F9FAFB] border-b border-[#E5E7EB] last:border-b-0 text-left transition-colors disabled:opacity-50">
              <div className="w-9 h-9 shrink-0 rounded-lg bg-[#F3F4F6] flex items-center justify-center">
                <Package size={16} className="text-[#6B7280]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0F1829] truncate">{p.Descricao}</p>
                <p className="text-xs text-[#6B7280] mt-0.5">#{p.ID_Produto}{p.Referencia ? ` · ${p.Referencia}` : ""}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-[#0F1829]">{fmt(Number(p.PrecoUnitario) || 0)}</p>
              </div>
              <div className="w-8 h-8 shrink-0 rounded-lg bg-[#0F1829] text-white flex items-center justify-center">
                {addingId === p.ID_Produto ? <SpinnerGap size={14} className="animate-spin" /> : <Plus size={14} />}
              </div>
            </button>
          ))}
        </div>
      )}

      {open && !loading && q.trim().length >= 2 && results.length === 0 && (
        <div className="absolute left-0 right-0 mt-1 bg-white rounded-xl border border-[#E5E7EB] shadow-xl z-20 p-4 text-center text-[#6B7280] text-xs"
          data-testid="product-search-empty">
          Sem resultados para &laquo;{q}&raquo;.
        </div>
      )}
    </div>
  );
}
